import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { useAuth } from "@/context/AuthContext";
import api, { fileUrl } from "@/lib/api";
import { useSeo } from "@/lib/seo";
import { toast } from "sonner";
import {
  Backpack, PencilRuler, Chalkboard, Buildings, GraduationCap, ChatCircleDots,
  Heart, ArrowRight, Sparkle, Clock, Camera, Question, MapPin, PlusCircle, CaretRight, Trophy, Medal,
} from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import MemoryContributeDialog from "@/components/MemoryContributeDialog";

const IMG = {
  hero: "https://images.unsplash.com/photo-1635424239131-32dc44986b56?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600",
  classroom: "https://images.unsplash.com/photo-1519406596751-0a3ccc4937fe?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
  uniform: "https://images.unsplash.com/photo-1619910114967-593e13f2487f?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
  supplies: "https://images.unsplash.com/photo-1471107340929-a87cd0f5b5f3?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
  building: "https://images.unsplash.com/photo-1621890849040-20837568b0e9?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
};

const THEMES = [
  { icon: Backpack, cat: "School Uniform", title: "Remember what you wore", body: "The colour of the shirt. The style of the skirt or trousers. The tie, the badge on the pocket, the winter sweater, the shoes everyone had to wear. Sometimes “we wore that!” is all it takes to bring a memory back." },
  { icon: PencilRuler, cat: "School Supplies", title: "The supplies we remember", body: "Fountain pens and ink bottles. Erasers and sharpeners. Geometry boxes, slate boards, exercise books, school diaries, lunch boxes and backpacks. Some have vanished; others changed completely." },
  { icon: Chalkboard, cat: "Classroom & Furniture", title: "The classroom itself", body: "Rows of wooden desks. The blackboard. The teacher's desk. The windows, the fan, the school clock, the maps on the wall — and the bell that ended every day." },
  { icon: Buildings, cat: "School / College Building", title: "Old buildings & places", body: "The front gate. The assembly hall. The playground, the library, the science lab, the principal's office, the staircase. A photo from 40 years ago can help someone recognise a place they haven't seen since childhood." },
];

const STORY = [
  { h: "A Museum of School Days", p: [
    "Imagine a digital collection where generations of students can explore the everyday world of school life from different decades and countries.",
    "Discover school uniforms, shoes, bags, books, notebooks, stationery, lunchboxes, badges, ties, sports uniforms, classroom furniture and other objects that defined a particular time and place. What seems ordinary today can become fascinating history tomorrow.",
  ], list: ["A school uniform from the 1960s.", "A wooden school desk from the 1970s.", "A textbook from the 1980s.", "A school badge from the 1990s.", "A familiar pencil box from the 2000s."], listNote: "Each item tells a story." },
  { h: "Every Photograph Has a Story", p: [
    "We don't want this to become simply a collection of photographs. We want to know the stories behind them.",
    "Who took the photograph? What year was it? Which class used the room? Who remembers the teacher? What was the uniform that year? A photograph becomes far more valuable when the people who lived that moment can tell its story.",
  ] },
  { h: "A Place to Talk About the Past", p: [
    "Memory Lane gives alumni a dedicated space to share memories and talk with others who remember the same time. One memory can lead to another. One photograph can bring an entire class back together.",
  ], list: [
    "“Does anyone remember the old blue school bags from the 1980s?”",
    "“Was this our classroom in 1978?”",
    "“Who remembers Mr. Thomas's science class?”",
    "“I found my old school badge after 35 years!”",
  ] },
  { h: "Compare Generations", p: [
    "School memories are different for every generation. Parents can show children what school looked like when they were young. Grandparents can share photographs from their school days.",
    "A student today might be surprised to discover that their grandparents once studied in the same building — perhaps in a classroom that no longer exists. School history becomes personal history.",
  ] },
  { h: "Preserve Before It Disappears", p: [
    "Old photographs fade. Buildings are renovated or demolished. Uniforms change, textbooks disappear, and the people who remember the details grow older.",
    "This is why preserving these memories matters. Memory Lane can become a digital museum of everyday educational life — built not only from official archives, but from the memories and contributions of the people who lived them.",
  ] },
];

const PROMPTS = [
  "What colour was your school uniform?", "Do you remember your first school bag?",
  "What kind of desks did you use?", "Who was your favourite teacher?",
  "Where did everyone sit during lunch?", "What did your school badge look like?",
  "Do you still have an old school photograph?", "What school tradition do you miss?",
];

function timeAgo(iso) {
  const d = (Date.now() - new Date(iso).getTime()) / 1000;
  if (d < 60) return "just now";
  if (d < 3600) return `${Math.floor(d / 60)}m ago`;
  if (d < 86400) return `${Math.floor(d / 3600)}h ago`;
  return `${Math.floor(d / 86400)}d ago`;
}

function MemoryCard({ m, onLike }) {
  const cover = (m.photo_file_ids || [])[0];
  return (
    <div className="flex flex-col overflow-hidden rounded-2xl border border-border/60 bg-card shadow-sm" data-testid={`memory-card-${m.id}`}>
      {cover && (
        <Link to={`/memory-lane/${m.id}`} className="block">
          <img src={fileUrl(cover)} alt={m.title} className="h-48 w-full object-cover" loading="lazy" />
        </Link>
      )}
      <div className="flex flex-1 flex-col p-5">
        <div className="flex flex-wrap items-center gap-2 text-xs">
          <span className="rounded-full bg-accent px-2.5 py-0.5 font-medium text-primary">{m.category}</span>
          {m.decade && <span className="rounded-full border border-border/60 px-2.5 py-0.5 text-muted-foreground">{m.decade}</span>}
        </div>
        <Link to={`/memory-lane/${m.id}`}><h3 className="mt-2 font-heading text-lg font-medium leading-tight hover:text-secondary">{m.title}</h3></Link>
        {m.memory && <p className="mt-1 line-clamp-3 text-sm text-muted-foreground">{m.memory}</p>}
        {(m.institution || m.city || m.country) && (
          <p className="mt-2 flex items-center gap-1 text-xs text-secondary"><MapPin size={12} /> {[m.institution, m.city, m.country].filter(Boolean).join(" · ")}</p>
        )}
        <div className="mt-auto flex items-center justify-between pt-4 text-xs text-muted-foreground">
          <span>{m.created_by_name} · {timeAgo(m.created_at)}</span>
          <button onClick={() => onLike(m)} className="flex items-center gap-1 hover:text-secondary" data-testid={`memory-like-${m.id}`}>
            <Heart size={15} weight={m._liked ? "fill" : "regular"} className={m._liked ? "text-secondary" : ""} /> {(m.likes || []).length}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function MemoryLane() {
  useSeo("Memory Lane", "A global digital museum of school & college memories — uniforms, classrooms, supplies, buildings, photographs and the stories behind them.");
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [defaultCat, setDefaultCat] = useState(null);
  const [memories, setMemories] = useState([]);
  const [awards, setAwards] = useState([]);
  const [facets, setFacets] = useState({ total: 0, categories: [], decades: [] });
  const [filter, setFilter] = useState({ category: "", decade: "" });

  const load = () => {
    api.get("/public/memories", { params: { category: filter.category, decade: filter.decade } })
      .then((r) => setMemories(r.data.map((m) => ({ ...m, _liked: user && (m.likes || []).includes(user.id) })))).catch(() => setMemories([]));
    api.get("/public/memories", { params: { category: "Trophies & Awards" } })
      .then((r) => setAwards(r.data)).catch(() => setAwards([]));
    api.get("/public/memories/facets").then((r) => setFacets(r.data)).catch(() => {});
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter.category, filter.decade]);

  const contribute = (cat) => {
    if (!user) { toast.info("Sign in to share your memory"); return; }
    setDefaultCat(cat || null); setOpen(true);
  };
  const ContributeBtn = ({ testid, cat, children, ...rest }) => (
    user
      ? <Button onClick={() => contribute(cat)} data-testid={testid} {...rest}>{children}</Button>
      : <Link to="/register"><Button data-testid={testid} {...rest}>{children}</Button></Link>
  );

  const onLike = async (m) => {
    if (!user) { toast.info("Sign in to react"); return; }
    try {
      const { data } = await api.post(`/memories/${m.id}/like`);
      setMemories((rows) => rows.map((r) => r.id === m.id ? { ...r, _liked: data.liked, likes: data.liked ? [...(r.likes || []), user.id] : (r.likes || []).filter((x) => x !== user.id) } : r));
    } catch { toast.error("Could not react"); }
  };

  return (
    <div className="bg-background" data-testid="memory-lane-page">
      {/* Top panel banner (Memory Lane pages only) */}
      <section className="border-b border-border/60 bg-[#efe7d6]">
        <img src="/memory-lane-banner.webp" alt="Alumni Pulse — School & College Memory Lane · Remembering the days that shaped us" className="mx-auto block w-full max-w-[1600px]" data-testid="memory-lane-banner" />
      </section>

      {/* Intro */}
      <section className="mx-auto max-w-4xl px-5 py-14 text-center">
        <p className="overline text-secondary mb-4 inline-flex items-center gap-1.5"><Clock size={14} weight="duotone" /> Remembering the days that shaped us</p>
        <h1 className="font-heading text-4xl font-light leading-[1.1] tracking-tight sm:text-5xl">
          Step back into the classroom — <span className="italic font-medium text-secondary">and rediscover the little things.</span>
        </h1>
        <p className="mx-auto mt-6 max-w-2xl text-base leading-relaxed text-muted-foreground sm:text-lg">
          Long after we leave school, the strongest memories aren't in report cards. They live in the uniform we wore, the bag we carried, the notebooks we filled, the lunchbox we shared, and the building we walked into every morning. AlumniPulse™ wants to preserve those memories.
        </p>
        <div className="mt-9 flex flex-wrap items-center justify-center gap-3">
          <ContributeBtn size="lg" className="rounded-full gap-2 px-7" testid="hero-contribute-cta"><Camera size={18} /> Share a memory</ContributeBtn>
          <a href="#archive"><Button size="lg" variant="outline" className="rounded-full px-7">Explore the archive</Button></a>
        </div>
      </section>

      {/* Theme cards */}
      <section className="mx-auto max-w-7xl px-5 py-16">
        <div className="mb-8 max-w-2xl">
          <p className="overline text-secondary mb-2">A museum of school days</p>
          <h2 className="font-heading text-3xl font-light tracking-tight">The everyday world of <span className="italic font-medium text-secondary">school & college life.</span></h2>
        </div>
        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {THEMES.map((t, i) => (
            <motion.button
              key={t.cat} onClick={() => contribute(t.cat)}
              initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5, delay: i * 0.08 }}
              className="group flex flex-col rounded-2xl border border-border/60 bg-card p-6 text-left shadow-sm transition-colors hover:border-secondary/40"
              data-testid={`theme-${i}`}
            >
              <span className="mb-4 inline-grid h-12 w-12 place-items-center rounded-xl bg-accent text-primary transition-colors group-hover:bg-secondary group-hover:text-secondary-foreground"><t.icon size={24} weight="duotone" /></span>
              <h3 className="font-heading text-lg font-medium">{t.title}</h3>
              <p className="mt-2 flex-1 text-sm leading-relaxed text-muted-foreground">{t.body}</p>
              <span className="mt-4 inline-flex items-center gap-1 text-sm font-medium text-secondary">Add to {t.cat} <CaretRight size={13} /></span>
            </motion.button>
          ))}
        </div>
      </section>

      {/* Editorial + imagery */}
      <section className="mx-auto max-w-7xl px-5 pb-4">
        <div className="grid gap-4 sm:grid-cols-3">
          {[["uniform", "School uniforms across the decades"], ["classroom", "Classrooms, then and now"], ["building", "The buildings we grew up in"]].map(([k, cap]) => (
            <div key={k} className="relative overflow-hidden rounded-2xl border border-border/60">
              <img src={IMG[k]} alt={cap} className="h-52 w-full object-cover" loading="lazy" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
              <p className="absolute bottom-4 left-4 right-4 font-heading text-sm font-medium text-white">{cap}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="mx-auto max-w-3xl px-5 py-16">
        <div className="space-y-12">
          {STORY.map((s) => (
            <div key={s.h}>
              <h2 className="font-heading text-2xl font-light tracking-tight sm:text-3xl">{s.h}</h2>
              <div className="mt-3 space-y-3">
                {s.p.map((p, i) => <p key={i} className="leading-relaxed text-muted-foreground">{p}</p>)}
              </div>
              {s.list && (
                <ul className="mt-4 space-y-2">
                  {s.list.map((li) => (
                    <li key={li} className="flex items-start gap-2.5 text-foreground/85"><Sparkle size={15} weight="fill" className="mt-1 shrink-0 text-secondary" /> {li}</li>
                  ))}
                </ul>
              )}
              {s.listNote && <p className="mt-3 font-heading text-secondary">{s.listNote}</p>}
            </div>
          ))}
        </div>
      </section>

      {/* Do You Remember */}
      <section className="mx-auto max-w-7xl px-5 pb-4">
        <div className="rounded-[2rem] border border-secondary/25 bg-accent/40 p-8 sm:p-12">
          <div className="flex items-center gap-3">
            <span className="inline-grid h-12 w-12 place-items-center rounded-2xl bg-secondary/15 text-secondary"><Question size={24} weight="duotone" /></span>
            <div>
              <p className="overline text-secondary">Do you remember?</p>
              <h2 className="font-heading text-2xl font-light tracking-tight sm:text-3xl">Every question can unlock a memory.</h2>
            </div>
          </div>
          <div className="mt-8 grid gap-3 sm:grid-cols-2">
            {PROMPTS.map((q) => (
              <button key={q} onClick={() => contribute()} className="flex items-center justify-between rounded-xl border border-border/60 bg-card px-4 py-3 text-left text-sm text-foreground/85 transition-colors hover:border-secondary/40 hover:text-secondary" data-testid="prompt-item">
                {q} <PlusCircle size={16} className="shrink-0 text-secondary" />
              </button>
            ))}
          </div>
          <p className="mt-6 text-sm text-muted-foreground">Add your own memory and photographs — and keep the past alive for the next generation.</p>
        </div>
      </section>

      {/* Trophies & Awards */}
      <section className="mx-auto max-w-7xl px-5 py-4">
        <div className="rounded-[2rem] border border-amber-500/25 bg-gradient-to-br from-amber-500/[0.07] to-secondary/[0.05] p-8 sm:p-12">
          <div className="flex flex-wrap items-end justify-between gap-4">
            <div className="max-w-2xl">
              <p className="overline text-amber-600 mb-2 inline-flex items-center gap-1.5"><Trophy size={15} weight="duotone" /> Trophies & Awards</p>
              <h2 className="font-heading text-3xl font-light tracking-tight">The wins the school and its students <span className="italic font-medium text-secondary">never forgot.</span></h2>
              <p className="mt-3 text-muted-foreground">Sports trophies, academic honours, competition medals, house cups and proud team moments. Add the title, the year, the students or team who won it, a photo, and the story behind it.</p>
            </div>
            <ContributeBtn cat="Trophies & Awards" className="rounded-full gap-1.5 bg-amber-600 text-white hover:bg-amber-700" testid="award-contribute-cta"><Medal size={16} /> Add an award</ContributeBtn>
          </div>

          {awards.length > 0 && (
            <div className="mt-8 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
              {awards.slice(0, 6).map((a) => {
                const cover = (a.photo_file_ids || [])[0];
                return (
                  <Link key={a.id} to={`/memory-lane/${a.id}`} className="flex flex-col overflow-hidden rounded-2xl border border-border/60 bg-card shadow-sm transition-colors hover:border-amber-500/40" data-testid={`award-card-${a.id}`}>
                    {cover ? (
                      <div className="relative"><img src={fileUrl(cover)} alt={a.title} className="h-44 w-full object-cover" loading="lazy" />
                        <span className="absolute left-3 top-3 grid h-9 w-9 place-items-center rounded-full bg-amber-500 text-white shadow"><Trophy size={18} weight="fill" /></span></div>
                    ) : (
                      <div className="grid h-44 w-full place-items-center bg-amber-500/10"><Trophy size={40} weight="duotone" className="text-amber-600" /></div>
                    )}
                    <div className="flex flex-1 flex-col p-5">
                      <div className="flex items-center gap-2 text-xs">
                        {a.period && <span className="rounded-full bg-amber-500/15 px-2.5 py-0.5 font-medium text-amber-700">{a.period}</span>}
                        {a.decade && <span className="rounded-full border border-border/60 px-2.5 py-0.5 text-muted-foreground">{a.decade}</span>}
                      </div>
                      <h3 className="mt-2 font-heading text-lg font-medium leading-tight">{a.title}</h3>
                      {a.people_shown && <p className="mt-1 flex items-center gap-1 text-sm text-secondary"><Medal size={13} /> {a.people_shown}</p>}
                      {a.memory && <p className="mt-1 line-clamp-2 text-sm text-muted-foreground">{a.memory}</p>}
                      {(a.institution || a.city) && <p className="mt-auto flex items-center gap-1 pt-3 text-xs text-muted-foreground"><MapPin size={12} /> {[a.institution, a.city].filter(Boolean).join(" · ")}</p>}
                    </div>
                  </Link>
                );
              })}
            </div>
          )}
        </div>
      </section>

      {/* Archive feed */}
      <section id="archive" className="mx-auto max-w-7xl px-5 py-16">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div className="max-w-2xl">
            <p className="overline text-secondary mb-2 inline-flex items-center gap-1.5"><Camera size={14} weight="duotone" /> The living archive</p>
            <h2 className="font-heading text-3xl font-light tracking-tight">Memories shared by the community <span className="italic font-medium text-secondary">({facets.total})</span></h2>
          </div>
          <ContributeBtn className="rounded-full gap-1.5" testid="archive-contribute-cta"><Camera size={16} /> Share yours</ContributeBtn>
        </div>

        {/* Filters */}
        {(facets.categories.length > 0 || facets.decades.length > 0) && (
          <div className="mt-6 flex flex-wrap items-center gap-2">
            <button onClick={() => setFilter({ category: "", decade: "" })} className={`rounded-full px-3 py-1 text-xs ${!filter.category && !filter.decade ? "bg-primary text-primary-foreground" : "border border-border/60 text-muted-foreground hover:text-foreground"}`}>All</button>
            {facets.categories.map((c) => (
              <button key={c.value} onClick={() => setFilter((f) => ({ ...f, category: f.category === c.value ? "" : c.value }))}
                className={`rounded-full px-3 py-1 text-xs ${filter.category === c.value ? "bg-secondary text-secondary-foreground" : "border border-border/60 text-muted-foreground hover:text-foreground"}`}>{c.value} ({c.count})</button>
            ))}
            {facets.decades.map((d) => (
              <button key={d.value} onClick={() => setFilter((f) => ({ ...f, decade: f.decade === d.value ? "" : d.value }))}
                className={`rounded-full px-3 py-1 text-xs ${filter.decade === d.value ? "bg-secondary text-secondary-foreground" : "border border-border/60 text-muted-foreground hover:text-foreground"}`}>{d.value}</button>
            ))}
          </div>
        )}

        {memories.length === 0 ? (
          <div className="mt-12 grid place-items-center rounded-2xl border border-dashed border-border/70 py-20 text-center">
            <Camera size={40} weight="duotone" className="text-muted-foreground" />
            <p className="mt-3 text-muted-foreground">No memories here yet. Be the first to add one.</p>
            <ContributeBtn variant="secondary" className="mt-4 rounded-full gap-1.5" testid="empty-contribute-cta"><Camera size={16} /> Share a memory</ContributeBtn>
          </div>
        ) : (
          <div className="mt-8 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {memories.map((m) => <MemoryCard key={m.id} m={m} onLike={onLike} />)}
          </div>
        )}
      </section>

      {/* Closing */}
      <section className="mx-auto max-w-7xl px-5 pb-24">
        <div className="rounded-[2rem] bg-primary px-8 py-14 text-center text-primary-foreground sm:px-12">
          <GraduationCap size={36} weight="duotone" className="mx-auto opacity-90" />
          <h2 className="mt-4 font-heading text-3xl font-light sm:text-4xl">Your school. Your memories. Your story.</h2>
          <p className="mx-auto mt-3 max-w-xl text-primary-foreground/80">Bring back the uniform. Find the old classroom. Remember the school bag. Share the photograph. Tell the story. Because sometimes the smallest things bring back the biggest memories.</p>
          <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
            <ContributeBtn size="lg" variant="secondary" className="rounded-full gap-2 px-8" testid="closing-contribute-cta"><Camera size={18} /> Share a memory</ContributeBtn>
            <Link to="/alumni-groups"><Button size="lg" variant="ghost" className="rounded-full px-8 text-primary-foreground hover:bg-primary-foreground/10 hover:text-primary-foreground">Find your class</Button></Link>
          </div>
        </div>
      </section>

      <MemoryContributeDialog open={open} onOpenChange={setOpen} defaultCategory={defaultCat} onCreated={load} />
    </div>
  );
}
